/* eslint-disable @typescript-eslint/naming-convention */
'use client';

import Image from 'next/image';
import Webcam from 'react-webcam';
import React, { useCallback, useEffect, useRef, useState } from 'react';

import { screensMap } from '..';
import { getPlanStatus } from '../EmiPlans/helper';
import DigiLockerSuccess from '../DigiLockerSuccess';
import Button from '@/lib/Button';
import Icon from '@/lib/Icon/Icon';
import InfoBox from '@/lib/InfoBox';
import { doSelfieKYC } from '@/api/kyc';
import TipsModal from '@/views/SelfieKYC/TipsModal';
import { MAX_SELFIE_KYC_RETRIES } from '@/constants';
import ErrorModal from '@/shared/Components/ErrorModal';
import StatusChecks from '@/views/SelfieKYC/StatusChecks';
import ShutterImage from '@/views/SelfieKYC/resources/camera-shutter.svg';
import OpenPopupLoader from '@/shared/Components/OpenPopupLoader';
import { useGlobalStore } from '@/store';
import { PaymentCancelWrapper } from '@/shared/Components/PaymentCancelWrapper';
import { PopupFailureProps } from '@/shared/types';
import { getUserLoggedInStatus, trackHelper } from '@/analytics/utils';
import { ANALYTICS_ACTIONS, EVENT_NAMES } from '@/analytics/constants';
import { EmiPlans } from '@/api/types';
import { updatePayment } from '@/api/payment';
import { formRequest } from '@/utils/helpers';

interface SelfieKYCError {
  title: string;
  description: string;
  cta: string;
  onClick: () => unknown;
}

const SelfieKYC: React.FC = () => {
  const webcamRef = useRef<Webcam>(null);
  const digiLockerUrl = useRef<string | null>(null);
  const [openDigiLocker, setOpenDigiLocker] = useState(false);
  const [error, setError] = useState<SelfieKYCError | null>(null);
  const [triesLeft, setTriesLeft] = useState(MAX_SELFIE_KYC_RETRIES);
  const [isTipsModalVisible, setIsTipsModalVisible] = useState(false);
  const setActiveView = useGlobalStore((store) => store.setActiveView);
  const setEmandateUrl = useGlobalStore((store) => store.setEMandateUrl);
  const [imageStatus, setImageStatus] = useState<ImageStatus>('capturing');
  const [imageSrc, setImageSrc] = useState<string | undefined>('');
  const setApprovedCreditLimit = useGlobalStore((store) => store.setApprovedCreditLimit);
  const [haveCameraPermission, setHaveCameraPermission] = useState(false);
  const [didSelfieKycFail, setDidSelfieKycFail] = useState(false);
  const [kycError, setKycError] = useState('');
  const { user, selectedTenure, lenderDetails, userAccessToken, activeView } = useGlobalStore();
  const [state, setState] = useState({
    imageCaptured: false,
  });

  const captureImage = useCallback(async () => {
    setImageStatus('processing');
    const imageSource = webcamRef.current?.getScreenshot({ width: 720, height: 542 });
    setImageSrc(imageSource as string);
    setState({
      imageCaptured: true,
    });
    setTriesLeft((currentTries) => currentTries - 1);

    try {
      const selfieKycResponse = await doSelfieKYC(imageSource as string);
      if (selfieKycResponse.success && selfieKycResponse.url) {
        setImageStatus('success');
        digiLockerUrl.current = selfieKycResponse.url;
      } else if (selfieKycResponse.error === 'KYC_FAILED') {
        setImageStatus('failure');
        // Track user selfie capture failure
        trackHelper({
          objectName: EVENT_NAMES.SELFIE_KYC_FAILED,
          actionName: ANALYTICS_ACTIONS.CLICK,
          screen: screensMap.SELFIE_KYC,
          properties: {
            fc_customer_id: user.id,
            selected_plan: selectedTenure,
            user_status: getPlanStatus(lenderDetails, selectedTenure as EmiPlans),
            user_type: getUserLoggedInStatus(userAccessToken, user.contact),
            contact: user.contact,
            reason: selfieKycResponse.error,
          },
        });
      } else if (selfieKycResponse.error === 'MAX_RETRY_LIMIT_REACHED') {
        setImageStatus('failure');
        setTriesLeft(0);
        // Track user selfie capture failure
        trackHelper({
          objectName: EVENT_NAMES.SELFIE_KYC_FAILED,
          actionName: ANALYTICS_ACTIONS.CLICK,
          screen: screensMap.SELFIE_KYC,
          properties: {
            fc_customer_id: user.id,
            selected_plan: selectedTenure,
            user_status: getPlanStatus(lenderDetails, selectedTenure as EmiPlans),
            user_type: getUserLoggedInStatus(userAccessToken, user.contact),
            contact: user.contact,
            reason: selfieKycResponse.error,
          },
        });
      }
    } catch {
      setImageStatus('failure');
    }

    // Track user selfie capture
    trackHelper({
      objectName: EVENT_NAMES.CAPTURE_SELFIE,
      actionName: ANALYTICS_ACTIONS.CLICK,
      screen: screensMap.SELFIE_KYC,
      properties: {
        fc_customer_id: user.id,
        selected_plan: selectedTenure,
        user_status: getPlanStatus(lenderDetails, selectedTenure as EmiPlans),
        user_type: getUserLoggedInStatus(userAccessToken, user.contact),
        contact: user.contact,
      },
    });
  }, [webcamRef]);

  const retakeSelfie = (): void => {
    if (triesLeft > 0) {
      setImageStatus('capturing');
      setImageSrc('');
      // Track retake selfie click
      trackHelper({
        objectName: EVENT_NAMES.RETAKE_SELFIE_CLICKED,
        actionName: ANALYTICS_ACTIONS.CLICK,
        screen: screensMap.SELFIE_KYC,
        properties: {
          fc_customer_id: user.id,
          selected_plan: selectedTenure,
          user_status: getPlanStatus(lenderDetails, selectedTenure as EmiPlans),
          user_type: getUserLoggedInStatus(userAccessToken, user.contact),
          contact: user.contact,
        },
      });
    }
  };

  const continueToDigiLocker = (): void => {
    setOpenDigiLocker(true);
    // Track ucontinue to digilocker
    trackHelper({
      objectName: EVENT_NAMES.CONTINUE_TO_DIGILOCKER,
      actionName: ANALYTICS_ACTIONS.CLICK,
      screen: screensMap.SELFIE_KYC,
      properties: {
        fc_customer_id: user.id,
        selected_plan: selectedTenure,
        user_status: getPlanStatus(lenderDetails, selectedTenure as EmiPlans),
        user_type: getUserLoggedInStatus(userAccessToken, user.contact),
        contact: user.contact,
      },
    });
  };

  const getCameraPermission = async (): Promise<void> => {
    setError(null);
    try {
      await navigator.mediaDevices.getUserMedia({ video: true });
      setHaveCameraPermission(true);
    } catch (e: any) {
      if (e.toString().includes('NotAllowedError')) {
        setError({
          title: 'Camera Access Denied',
          cta: 'Retry',
          description: 'Please allow camera access to retry capturing selfie.',
          onClick: getCameraPermission,
        });
      }
    }
  };

  const handleCameraFailure = (cameraError: string): void => {
    if (typeof cameraError === 'string') {
      if (cameraError.includes('NotAllowedError')) {
        getCameraPermission();
        // Track Camera access permission requested
        trackHelper({
          objectName: EVENT_NAMES.CAMERA_ACCESS_CLICK,
          actionName: ANALYTICS_ACTIONS.CLICK,
          screen: screensMap.SELFIE_KYC,
          properties: {
            fc_customer_id: user.id,
            selected_plan: selectedTenure,
            user_status: getPlanStatus(lenderDetails, selectedTenure as EmiPlans),
            user_type: getUserLoggedInStatus(userAccessToken, user.contact),
            contact: user.contact,
          },
        });
      }
    } else {
      setError({
        title: 'Unknown Error',
        description: 'Some unknown error has occurred',
        cta: 'Retry',
        onClick: () => {
          setError(null);
          setImageStatus('capturing');
        },
      });
    }
  };

  const handleOpenDigiLockerPopup = (data: any): void => {
    if (!data) {
      trackHelper({
        objectName: EVENT_NAMES.SELFIE_KYC_FAILED,
        actionName: ANALYTICS_ACTIONS.CLICK,
        screen: screensMap.SELFIE_KYC,
        properties: {
          fc_customer_id: user.id,
          selected_plan: selectedTenure,
          user_status: getPlanStatus(lenderDetails, selectedTenure as EmiPlans),
          user_type: getUserLoggedInStatus(userAccessToken, user.contact),
          contact: user.contact,
          reason: 'Digilocker KYC failed',
        },
      });
      return setDidSelfieKycFail(true);
    }
    const creditLimit = data.approved_amount as unknown as number;

    setApprovedCreditLimit(creditLimit);
    setOpenDigiLocker(false);
    setActiveView('DIGILOCKER_SUCCESS');
  };

  const handleKycFailure = (e: PopupFailureProps): void => {
    setDidSelfieKycFail(true);
    setOpenDigiLocker(false);
    if (e.message) {
      // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
      setKycError((e.message as any)?.description || 'Kyc could not be completed.');
      // Track user selfie capture failure
      trackHelper({
        objectName: EVENT_NAMES.SELFIE_KYC_FAILED,
        actionName: ANALYTICS_ACTIONS.CLICK,
        screen: screensMap.SELFIE_KYC,
        properties: {
          fc_customer_id: user.id,
          selected_plan: selectedTenure,
          user_status: getPlanStatus(lenderDetails, selectedTenure as EmiPlans),
          user_type: getUserLoggedInStatus(userAccessToken, user.contact),
          contact: user.contact,
          reason: e.message,
        },
      });
    }
  };

  const handleCancel = async (): Promise<void> => {
    try {
      const res = await updatePayment({
        status: 'failed',
        // eslint-disable-next-line @typescript-eslint/naming-convention
        failure_reason: 'cancelled_by_user',
      });
      if (res.data.gateway_payload) {
        const { url, payload, method } = res.data.gateway_payload;
        formRequest(url, payload, method);
        // Track Change Payment Method clicked on retry
        trackHelper({
          objectName: EVENT_NAMES.CHANGE_PAYMENT_METHOD,
          actionName: ANALYTICS_ACTIONS.CLICK,
          screen: activeView,
        });
      }
    } catch {
      //
    }
  };

  useEffect(() => {
    getCameraPermission();
    // Track Selfie Kyc screen render
    trackHelper({
      actionName: ANALYTICS_ACTIONS.RENDER,
      objectName: EVENT_NAMES.SELFIE_SCREEN_RENDER,
      screen: screensMap.SELFIE_KYC,
      properties: {
        fc_customer_id: user.id,
        selected_plan: selectedTenure,
        user_status: getPlanStatus(lenderDetails, selectedTenure as EmiPlans),
        user_type: getUserLoggedInStatus(userAccessToken, user.contact),
        contact: user.contact,
      },
    });
  }, []);

  return (
    <div className="d:h-full d:flex d:items-center d:justify-center">
      <div className="px-6 pb-8 pt-1">
        <h2 className="font-semibold font-tasa text-2xl text-on-grey-normal">
          Complete selfie verification and Aadhaar KYC
        </h2>

        <div className="grow flex flex-col justify-center">
          <div
            className={`rounded-full overflow-hidden border-4 mt-5 mb-4 w-full mx-auto aspect-square bg-black-normal
            ${imageStatus === 'capturing' ? 'border-outline-grey-subtle' : ''}
            ${
              ['processing', 'success'].includes(imageStatus)
                ? 'border-positive-normal max-w-fit sm:max-w-fit'
                : 'sm:max-w-[350px] max-w-[250px] checkoutV2:max-w-[240px]'
            }
            ${
              (['success'].includes(imageStatus) || state.imageCaptured) &&
              imageStatus !== 'capturing'
                ? 'hidden'
                : ''
            }
            ${imageStatus === 'failure' ? 'border-negative-normal' : ''}
            `}
          >
            {imageStatus === 'capturing' && haveCameraPermission ? (
              <Webcam
                audio={false}
                ref={webcamRef}
                screenshotFormat="image/jpeg"
                className="w-full h-full object-cover"
                onUserMediaError={(cameraError): void =>
                  handleCameraFailure(cameraError.toString())
                }
                videoConstraints={{
                  facingMode: 'user',
                }}
                mirrored={true}
              />
            ) : null}
          </div>
          {imageSrc && (imageStatus === 'success' || state.imageCaptured) ? (
            <div className="mt-5 mb-4 my-0 flex justify-center w-full">
              <div className="w-62 h-62 sm:w-[300px] sm:h-[300px] checkoutV2:max-w-[240px] checkoutV2:max-h-[240px] rounded-full border-4 border-positive-normal">
                <img
                  alt="imagesrc"
                  className="rounded-full h-full w-full object-cover sm:object-fill d:object-cover"
                  src={imageSrc}
                />
              </div>
            </div>
          ) : null}
          <div className="mt-4 mb-7">
            {imageStatus === 'capturing' ? (
              <>
                <p className="text-base text-black-normal font-semibold text-center">
                  Position your face fully within the frame
                </p>
                <button
                  onClick={(): void => setIsTipsModalVisible(true)}
                  className="text-base mx-auto block font-semibold text-on-primary-normal"
                >
                  Tips for a clear photo
                </button>
              </>
            ) : null}

            {imageStatus === 'failure' ? (
              <StatusChecks checks={['Liveliness check failed']} status={imageStatus} />
            ) : null}

            {imageStatus === 'success' ? (
              <StatusChecks checks={['Liveliness check success']} status={imageStatus} />
            ) : null}
          </div>
        </div>

        {imageStatus === 'capturing' ? (
          <div className="w-full">
            <div className="relative flex items-center mb-3">
              <button onClick={captureImage} data-testid="shutter-btn" className="mx-auto">
                <Image src={ShutterImage as string} alt="Shutter" />
              </button>
            </div>
            <p className="text-base text-black-normal font-semibold text-center">
              {triesLeft}/{MAX_SELFIE_KYC_RETRIES} chances remaining
            </p>
          </div>
        ) : null}

        {imageStatus === 'processing' ? (
          <Button isLoading loadingText="Reviewing Selfie">
            Reviewing Selfie
          </Button>
        ) : null}

        {imageStatus === 'failure' ? (
          <div>
            {triesLeft <= 0 ? (
              <InfoBox variant="warning">
                Reached maximum retakes. Retry after 24 hours or choose another payment method
              </InfoBox>
            ) : null}

            <Button disabled={triesLeft <= 0} onClick={retakeSelfie}>
              Retake Selfie
            </Button>

            {triesLeft <= 0 ? (
              <button
                onClick={handleCancel}
                className="text-on-primary-normal font-semibold m-auto block"
              >
                Change payment method
              </button>
            ) : (
              <p className="text-sm flex gap-1 items-center">
                <Icon name="info" />
                Maximum <strong>{MAX_SELFIE_KYC_RETRIES} selfie retakes</strong> allowed in 24 hours
              </p>
            )}
          </div>
        ) : null}

        {imageStatus === 'success' ? (
          <div>
            <Button onClick={continueToDigiLocker}>Continue to DigiLocker</Button>
            <Button className="mt-0" variant="secondary" onClick={retakeSelfie}>
              Retake Selfie
            </Button>
          </div>
        ) : null}

        {isTipsModalVisible ? (
          <TipsModal
            isOpen={isTipsModalVisible}
            onDismiss={(): void => setIsTipsModalVisible(false)}
          />
        ) : null}

        {openDigiLocker ? (
          <OpenPopupLoader
            onFail={handleKycFailure}
            progressText="KYC is in progress on"
            serviceType="kyc"
            serviceName="DigiLocker"
            onSuccess={handleOpenDigiLockerPopup}
            url={digiLockerUrl.current as string}
          />
        ) : null}

        {error ? (
          <ErrorModal
            title={error.title}
            description={error.description}
            ctaText={error.cta}
            isOpen={true}
            onDismiss={(): null => null}
            onCtaClick={(): unknown => error.onClick()}
          />
        ) : null}

        {didSelfieKycFail ? (
          <PaymentCancelWrapper
            isOpen={didSelfieKycFail}
            onDismiss={(): void => setDidSelfieKycFail(false)}
            ctaText="Change payment method"
            title="Selfie KYC failed"
            isCloseable={false}
            description={kycError || 'Selfie KYC failed. Try with a different payment method'}
          />
        ) : null}
      </div>
    </div>
  );
};

export default SelfieKYC;
